'use client';

import { useState } from 'react';
import KioskNav from '../shared/KioskNav';

interface PalletQuantityPageProps {
  shipperName: string;
  initialQuantity: string;
  initialUnit: string;
  onQuantityChange: (quantity: string) => void;
  onUnitChange: (unit: string) => void;
  onPrevious: () => void;
  onNext: () => void;
}

export default function PalletQuantityPage({
  shipperName,
  initialQuantity,
  initialUnit,
  onQuantityChange,
  onUnitChange,
  onPrevious,
  onNext,
}: PalletQuantityPageProps) {
  const [selectedUnit, setSelectedUnit] = useState(initialUnit || '1대분');
  const [customQuantity, setCustomQuantity] = useState(initialQuantity);

  const units = ['1대분', '2대분', '3대분', '4대분', '5대분', '6대분', '7대분', '8대분'];

  // props 변경 시 동기화 (렌더 중 조정 — effect 내 setState 회피)
  const [prevProps, setPrevProps] = useState({ initialUnit, initialQuantity });
  if (prevProps.initialUnit !== initialUnit || prevProps.initialQuantity !== initialQuantity) {
    setPrevProps({ initialUnit, initialQuantity });
    setSelectedUnit(initialUnit || '1대분');
    setCustomQuantity(initialQuantity);
  }

  const handleQuantityChange = (value: string) => {
    setCustomQuantity(value);
    onQuantityChange(value);
  };

  const handleUnitChange = (unit: string) => {
    setSelectedUnit(unit);
    onUnitChange(unit);
  };

  const handleNext = () => {
    if (customQuantity.trim()) {
      onNext();
    }
  };

  return (
    <div className="h-full bg-gray-50 flex flex-col justify-between py-6 px-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center space-x-10">
          <h1 className="text-5xl font-bold text-gray-800">화주명</h1>
          <div className="text-4xl font-semibold text-blue-600">{shipperName}</div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center space-y-8">
        {/* Quantity Input Section */}
        <div className="flex items-center space-x-10">
          <label className="text-4xl font-semibold text-gray-800">파레트 총 수량 :</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder=""
            value={customQuantity}
            onChange={(e) => handleQuantityChange(e.target.value)}
            className="w-64 h-16 text-3xl text-center bg-green-100 border-2 border-green-300 rounded-lg focus:outline-none focus:border-green-500"
          />
        </div>

        {/* Unit Selection Grid */}
        <div className="grid grid-cols-4 gap-4">
          {units.map((unit) => (
            <button
              key={unit}
              onClick={() => handleUnitChange(unit)}
              className={`w-36 h-14 text-2xl font-semibold rounded-lg transition-colors ${
                selectedUnit === unit
                  ? 'bg-black text-white hover:bg-gray-800'
                  : 'bg-white text-gray-800 border-2 border-gray-300 hover:bg-gray-100'
              }`}
            >
              {unit}
            </button>
          ))}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="flex flex-col items-center space-y-4">
        {/* Navigation Buttons */}
        <KioskNav
          onPrevious={onPrevious}
          onNext={handleNext}
          nextDisabled={!customQuantity.trim()}
        />

        {/* Bottom Instructions */}
        <p className="text-lg text-gray-600">터치스크린을 사용하여 입력해주세요</p>
      </div>
    </div>
  );
}
